import { configureStore } from '@reduxjs/toolkit'
import { combineReducers } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { fork } from 'redux-saga/effects'
// import selectors setup
import './modules/selectors'

import adAccounts from './modules/reducers/adAccounts'
import ads from './modules/reducers/ads'
import adServer from './modules/reducers/adServer'
import app from './modules/reducers/app'
import assets from './modules/reducers/assets'
import audiences from './modules/reducers/audiences'
import auth from './modules/reducers/auth'
import batchInfo from './modules/reducers/batchInfo'
import calendarPlanner from './modules/reducers/calendarPlanner'
import campaigns from './modules/reducers/campaigns'
import campaignsSummary from './modules/reducers/campaignsSummary'
import choices from './modules/reducers/choices'
import cognito from './modules/reducers/cognito'
import combinedData from './modules/reducers/combinedData'
import dv360Constants from './modules/reducers/dv360Constants'
import files from './modules/reducers/files'
import forms from './modules/reducers/forms'
import googleAdManager from './modules/reducers/googleAdManager'
import googleConstants from './modules/reducers/googleConstants'
import lineItems from './modules/reducers/lineItems'
import lineItemsSummary from './modules/reducers/lineItemsSummary'
import location from './modules/reducers/location'
import margins from './modules/reducers/margins'
import mediaFiles from './modules/reducers/mediaFiles'
import partnerships from './modules/reducers/partnerships'
import rights from './modules/reducers/rights'
import selfAccounts from './modules/reducers/selfAccounts'
import socialAccounts from './modules/reducers/socialAccounts'
import socialReturnOnInvestment from './modules/reducers/socialReturnOnInvestment'
import teamMembers from './modules/reducers/team'
import tikTokConstants from './modules/reducers/tikTokConstants'
import tools from './modules/reducers/tools'
import user from './modules/reducers/user'
import users from './modules/reducers/users'

import adAccountsWatcher from './modules/sagas/adAccounts'
import adsWatcher from './modules/sagas/ads'
import adServerWatcher from './modules/sagas/adServer'
import appWatcher from './modules/sagas/app'
import assetsWatcher from './modules/sagas/assets'
import audiencesWatcher from './modules/sagas/audiences'
import authWatcher from './modules/sagas/auth'
import batchInfoWatcher from './modules/sagas/batchInfo'
import calendarPlannerWatcher from './modules/sagas/calendarPlanner'
import campaignsSummaryWatcher from './modules/sagas/campaignsSummary'
import campaignsWatcher from './modules/sagas/campaigns'
import choicesWatcher from './modules/sagas/choices'
import cognitoWatcher from './modules/sagas/cognito'
import combinedDataWatcher from './modules/sagas/combinedData'
import dv360ConstantsWatcher from './modules/sagas/dv360Constants'
import filesWatcher from './modules/sagas/files'
import googleAdManagerWatcher from './modules/sagas/googleAdManager'
import googleConstantsWatcher from './modules/sagas/googleConstants'
import lineItemsSummaryWatcher from './modules/sagas/lineItemsSummary'
import lineItemsWatcher from './modules/sagas/lineItems'
import locationWatcher from './modules/sagas/location'
import marginsWatcher from './modules/sagas/margins'
import mediaFilesWatcher from './modules/sagas/mediaFiles'
import partnershipsWatcher from './modules/sagas/partnerships'
import rightsWatcher from './modules/sagas/rights'
import selfAccountsWatcher from './modules/sagas/selfAccounts'
import socialAccountsWatcher from './modules/sagas/socialAccounts'
import socialReturnOnInvestmentWatcher from './modules/sagas/socialReturnOnInvestment'
import teamMembersWatcher from './modules/sagas/team'
import tikTokConstantsWatcher from './modules/sagas/tikTokConstants'
import toolsWatcher from './modules/sagas/tools'
import userWatcher from './modules/sagas/user'
import usersWatcher from './modules/sagas/users'

import { SUBMIT_LOGOUT_SUCCESS } from './modules/actions/cognito'

const appReducer = combineReducers({
  adAccounts,
  ads,
  adServer,
  app,
  assets,
  audiences,
  auth,
  batchInfo,
  calendarPlanner,
  campaigns,
  campaignsSummary,
  choices,
  cognito,
  combinedData,
  dv360Constants,
  files,
  forms,
  googleAdManager,
  googleConstants,
  lineItems,
  lineItemsSummary,
  location,
  margins,
  mediaFiles,
  partnerships,
  rights,
  selfAccounts,
  socialAccounts,
  socialReturnOnInvestment,
  teamMembers,
  tikTokConstants,
  tools,
  user,
  users
})

const rootReducer = (state, action) => {
  if (action.type === SUBMIT_LOGOUT_SUCCESS) {
    state = undefined
  }

  return appReducer(state, action)
}

function* rootSaga() {
  yield fork(adAccountsWatcher)
  yield fork(adsWatcher)
  yield fork(adServerWatcher)
  yield fork(appWatcher)
  yield fork(assetsWatcher)
  yield fork(audiencesWatcher)
  yield fork(authWatcher)
  yield fork(batchInfoWatcher)
  yield fork(calendarPlannerWatcher)
  yield fork(campaignsSummaryWatcher)
  yield fork(campaignsWatcher)
  yield fork(choicesWatcher)
  yield fork(cognitoWatcher)
  yield fork(combinedDataWatcher)
  yield fork(dv360ConstantsWatcher)
  yield fork(filesWatcher)
  yield fork(googleAdManagerWatcher)
  yield fork(googleConstantsWatcher)
  yield fork(lineItemsSummaryWatcher)
  yield fork(lineItemsWatcher)
  yield fork(locationWatcher)
  yield fork(marginsWatcher)
  yield fork(mediaFilesWatcher)
  yield fork(partnershipsWatcher)
  yield fork(rightsWatcher)
  yield fork(selfAccountsWatcher)
  yield fork(socialAccountsWatcher)
  yield fork(socialReturnOnInvestmentWatcher)
  yield fork(teamMembersWatcher)
  yield fork(tikTokConstantsWatcher)
  yield fork(toolsWatcher)
  yield fork(userWatcher)
  yield fork(usersWatcher)
}

const sagaMiddleware = createSagaMiddleware()

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    // adding the saga middleware here
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: {
        ignoredPaths: ['cognito']
      }
    }).concat(sagaMiddleware)
})

sagaMiddleware.run(rootSaga)

export default store
